import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Form,
    Modal,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import InfoTooltip from '@/components/common/InfoTooltip';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import {
    getCommunity,
    requestNeedsModerators,
} from '@/shared/services/communityApi';
import {
    banMember,
    unbanMember,
    getMemberships,
    getApplications,
    decideApplication,
} from '@/shared/services/membershipApi';
import { useAuth } from '@/shared/contexts/AuthContext';
import { formatFullDate } from '@/shared/utils/dateUtils';
import './ModeratorDashboard.css';
import useModeratorCommunities from '@/hooks/useModeratorCommunities';

import ApplicationCard, { STATUS_BADGE } from './components/ApplicationCard';

const MEMBERS_PER_PAGE = 20;

export default function ModeratorMembers({
    communities: communitiesProp = null,
}) {
    const { moderatorCommunities: hookCommunities, loading: hookLoading } =
        useModeratorCommunities();

    const moderatorCommunities = communitiesProp ?? hookCommunities;
    const communitiesLoading = communitiesProp !== null ? false : hookLoading;
    const { isAdmin } = useAuth();

    const [needsInfo, setNeedsInfo] = useState({
        needsModerators: false,
        needsModeratorsRequested: false,
    });
    const [selectedCommunityId, setSelectedCommunityId] = useState('');
    const [members, setMembers] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Applications load independently of the roster, so they get their own
    // error slot — a failed applications fetch must not blank the members list
    // (which is gated on the shared `error`), and vice versa.
    const [applicationsError, setApplicationsError] = useState(null);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        action: '',
        reason: '',
        membership: null,
    });
    const [applicationModal, setApplicationModal] = useState({
        show: false,
        decision: '',
        reason: '',
        application: null,
        // Decision failures surface here, inside the modal, so the admin sees
        // them where they're acting — not behind the open modal in the
        // applications section.
        error: '',
    });
    const [historyModal, setHistoryModal] = useState({
        show: false,
        application: null,
    });
    const [showRequestConfirm, setShowRequestConfirm] = useState(false);

    const api = useAuthenticatedAxios();

    // Set initial community when list loads
    useEffect(() => {
        if (moderatorCommunities.length > 0 && !selectedCommunityId) {
            setSelectedCommunityId(moderatorCommunities[0].communityId);
        }
    }, [moderatorCommunities, selectedCommunityId]);

    const getRoleBadge = (membership) => {
        if (membership.user?.role === 'admin') {
            return (
                <Badge bg="warning" className="ms-2 text-dark">
                    Admin
                </Badge>
            );
        }
        if (membership.role === 'moderator') {
            return (
                <Badge bg="success" className="ms-2">
                    Moderator
                </Badge>
            );
        }
        if (membership.role === 'member') {
            return (
                <Badge bg="primary" className="ms-2">
                    Member
                </Badge>
            );
        }
        return null;
    };

    const fetchMembers = useCallback(
        async (communityId, currentOffset = 0) => {
            if (!communityId) return;
            try {
                setLoading(true);
                setError(null);
                const data = await getMemberships(
                    {
                        communityId,
                        limit: MEMBERS_PER_PAGE,
                        offset: currentOffset,
                    },
                    api
                );
                if (currentOffset === 0) {
                    setMembers(data);
                } else {
                    setMembers((prev) => [...prev, ...data]);
                }
                setHasMore(data.length === MEMBERS_PER_PAGE);
                setOffset(currentOffset + data.length);
            } catch (err) {
                console.error('Failed to fetch members:', err);
                setError(
                    err.response?.data?.message || 'Failed to load members'
                );
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const fetchApplications = useCallback(
        async (communityId) => {
            if (!communityId) return;
            try {
                setApplicationsError(null);
                const data = await getApplications(api, {
                    communityId,
                    status: 'pending',
                });
                setApplications(data);
            } catch (err) {
                console.error('Error fetching applications:', err);
                setApplicationsError(
                    err.response?.data?.message ||
                        'Failed to load moderator applications'
                );
            }
        },
        [api]
    );

    useEffect(() => {
        if (selectedCommunityId) {
            setOffset(0);
            setMembers([]);
            fetchMembers(selectedCommunityId, 0);
            fetchApplications(selectedCommunityId);
        }
    }, [selectedCommunityId, fetchMembers, fetchApplications]);

    useEffect(() => {
        if (!selectedCommunityId) return;
        let cancelled = false;
        getCommunity(selectedCommunityId)
            .then((community) => {
                if (cancelled) return;
                setNeedsInfo({
                    needsModerators: community.needsModerators,
                    needsModeratorsRequested:
                        community.needsModeratorsRequested,
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [selectedCommunityId]);

    const handleCommunityChange = (e) => {
        setSelectedCommunityId(e.target.value);
    };

    const handleLoadMore = () => {
        fetchMembers(selectedCommunityId, offset);
    };

    const handleBan = (membership) => {
        setConfirmModal({
            show: true,
            action: 'ban',
            reason: '',
            membership: membership,
        });
    };

    const confirmBan = async (reason) => {
        if (!confirmModal.membership) {
            setError('This user has no active membership to ban.');
            return;
        }
        const membershipId = confirmModal.membership._id;
        try {
            const data = await banMember(api, membershipId, reason);
            // Merge the server's audit fields (status, bannedBy, bannedReason,
            // bannedAt) into local state in place — mirrors CommunityPage so
            // an immediate Unban shows full context without a refetch, and so
            // a paginated roster isn't snapped back to page 1. Fall back to
            // just flipping status if an older server response omits the
            // membership.
            const auditFields = data?.membership ?? { status: 'banned' };
            setMembers((prev) =>
                prev.map((m) =>
                    m._id === membershipId ? { ...m, ...auditFields } : m
                )
            );
            closeConfirmModal();
        } catch (err) {
            const backendMessage = err.response?.data?.message;
            setError(backendMessage || 'Failed to ban member.');
        }
    };

    const handleUnban = (membership) => {
        setConfirmModal({
            show: true,
            action: 'unban',
            reason: '',
            membership: membership,
        });
    };

    const confirmUnban = async () => {
        if (!confirmModal.membership) {
            setError('This user has no active membership to unban.');
            return;
        }
        try {
            await unbanMember(api, confirmModal.membership._id);
            setMembers((prev) =>
                prev.map((m) =>
                    m._id === confirmModal.membership._id
                        ? { ...m, status: 'active' }
                        : m
                )
            );
            closeConfirmModal();
        } catch (err) {
            const backendMessage = err.response?.data?.message;
            setError(backendMessage || 'Failed to unban member.');
        }
    };

    const closeConfirmModal = () => {
        setConfirmModal({
            show: false,
            action: '',
            reason: '',
            membership: null,
        });
    };

    const handleAccept = (application) => {
        setApplicationModal({
            show: true,
            decision: 'accepted',
            reason: '',
            application,
            error: '',
        });
    };

    const handleReject = (application) => {
        setApplicationModal({
            show: true,
            decision: 'rejected',
            reason: '',
            application,
            error: '',
        });
    };

    const closeApplicationModal = () => {
        setApplicationModal({
            show: false,
            decision: '',
            reason: '',
            application: null,
            error: '',
        });
    };

    const confirmDecision = async () => {
        const { application, decision, reason } = applicationModal;
        if (!application) return;
        if (decision === 'rejected' && !reason.trim()) {
            setApplicationModal((prev) => ({
                ...prev,
                error: 'A reason is required to reject an application.',
            }));
            return;
        }
        try {
            await decideApplication(
                api,
                application.membershipId,
                application.applicationId,
                { decision, decisionReason: reason }
            );
            // The queue only shows pending applications, so once this one is
            // decided drop it from the list — no refetch needed.
            setApplications((prev) =>
                prev.filter(
                    (a) => a.applicationId !== application.applicationId
                )
            );
            // Accepting promotes the member to moderator server-side; mirror
            // that in the roster so their role badge updates immediately.
            if (decision === 'accepted') {
                setMembers((prev) =>
                    prev.map((m) =>
                        m._id === application.membershipId
                            ? { ...m, role: 'moderator' }
                            : m
                    )
                );
            }
            closeApplicationModal();
        } catch (err) {
            // Keep the modal open and show the failure inside it, so the admin
            // sees it where they acted rather than behind the modal.
            const backendMessage = err.response?.data?.message;
            setApplicationModal((prev) => ({
                ...prev,
                error: backendMessage || 'Failed to decide application.',
            }));
        }
    };

    const handleViewHistory = (application) => {
        setHistoryModal({ show: true, application });
    };

    const closeHistoryModal = () => {
        setHistoryModal({ show: false, application: null });
    };

    const handleRequestModerators = async () => {
        try {
            await requestNeedsModerators(api, selectedCommunityId);
            setNeedsInfo((prev) => ({
                ...prev,
                needsModeratorsRequested: true,
            }));
            setShowRequestConfirm(false);
        } catch (err) {
            setError(
                err.response?.data?.message || 'Failed to request moderators.'
            );
            setShowRequestConfirm(false);
        }
    };

    const alreadyRecruiting = needsInfo.needsModerators;
    const requestPending = needsInfo.needsModeratorsRequested;
    const requestDisabled = alreadyRecruiting || requestPending;
    const requestLabel = alreadyRecruiting
        ? 'Already recruiting'
        : requestPending
          ? 'Request pending'
          : 'Request moderators';

    if (communitiesLoading) {
        return (
            <div className="py-4">
                <Container>
                    <div className="d-flex justify-content-center p-5">
                        <Spinner animation="border" variant="light" />
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </Container>
            </div>
        );
    }

    if (moderatorCommunities.length === 0) {
        return (
            <div className="py-4">
                <Container>
                    <Alert variant="warning">
                        You are not currently a moderator of any communities.
                    </Alert>
                </Container>
            </div>
        );
    }

    const selectedCommunity = moderatorCommunities.find(
        (m) => m.communityId === selectedCommunityId
    );

    // A reason is mandatory when rejecting so the applicant gets actionable
    // feedback; it stays optional for acceptances.
    const rejectReasonMissing =
        applicationModal.decision === 'rejected' &&
        !applicationModal.reason.trim();

    return (
        <div className="py-4">
            <Container>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
                    <h2 className="fs-3 fw-bold text-white mb-0">Members</h2>

                    <div className="d-flex align-items-center flex-wrap gap-2">
                        {moderatorCommunities.length > 1 && (
                            <Form.Select
                                value={selectedCommunityId}
                                onChange={handleCommunityChange}
                                className="w-auto mod-community-select"
                                aria-label="Select community"
                            >
                                {moderatorCommunities.map((m) => (
                                    <option
                                        key={m.communityId}
                                        value={m.communityId}
                                    >
                                        {m.community?.name || m.communityId}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        <Button
                            variant="light"
                            className="text-black fw-light"
                            onClick={() => setShowRequestConfirm(true)}
                            disabled={requestDisabled}
                            aria-label={`Request moderators for ${
                                selectedCommunity?.community?.name ||
                                'this community'
                            }`}
                        >
                            {requestLabel}
                        </Button>
                    </div>
                </div>

                {moderatorCommunities.length === 1 && selectedCommunity && (
                    <p className="text-white-50 mb-4 small">
                        {selectedCommunity.community?.name}
                    </p>
                )}

                {error && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setError(null)}
                    >
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p className="mb-0">{error}</p>
                    </Alert>
                )}

                {!loading && !error && members.length === 0 && (
                    <Alert variant="info">
                        No active members found for this community.
                    </Alert>
                )}

                {members.map((membership) => (
                    <Card key={membership._id} className="mb-2 shadow-sm">
                        <Card.Body className="d-flex align-items-center justify-content-between py-2 px-3">
                            <div className="d-flex align-items-center gap-3">
                                {membership.user?.profilePicture ? (
                                    <img
                                        src={membership.user.profilePicture}
                                        alt=""
                                        className="rounded-circle object-fit-cover mod-avatar-sm"
                                        width="36"
                                        height="36"
                                    />
                                ) : (
                                    <div
                                        className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white mod-avatar-sm"
                                        aria-hidden="true"
                                    >
                                        <i className="bi bi-person-fill" />
                                    </div>
                                )}
                                <div>
                                    <p className="mb-0 fw-medium">
                                        {membership.user?.username
                                            ? `@${membership.user.username}`
                                            : 'Unknown user'}
                                    </p>
                                    {membership.user?.organization && (
                                        <p className="mb-0 text-body-secondary small">
                                            {membership.user.organization}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {membership.user?.role !== 'admin' && // Stops ban buttons from rendering on any admin's card
                                    membership.status !== 'banned' &&
                                    !(
                                        membership.role === 'moderator' && // Stops ban button from rendering on any moderator's card unless the currnent user is an admin.
                                        !isAdmin // With all of the conditions, users will never see ban buttons on their own cards
                                    ) && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() =>
                                                handleBan(membership)
                                            }
                                            aria-label={`Ban ${membership.user?.username || 'this member'}`}
                                        >
                                            <i
                                                className="bi bi-slash-circle me-1"
                                                aria-hidden="true"
                                            />
                                            Ban
                                        </Button>
                                    )}
                                {membership.user?.role !== 'admin' &&
                                    membership.status === 'banned' &&
                                    !(
                                        membership.role === 'moderator' &&
                                        !isAdmin
                                    ) && (
                                        <Button
                                            variant="warning"
                                            size="sm"
                                            className="text-dark"
                                            onClick={() =>
                                                handleUnban(membership)
                                            }
                                            aria-label={`Unban ${membership.user?.username || 'this member'}`}
                                        >
                                            <i
                                                className="bi bi-slash-circle me-1"
                                                aria-hidden="true"
                                            />
                                            Unban
                                        </Button>
                                    )}
                                {getRoleBadge(membership)}
                                {membership.status === 'banned' && (
                                    <Badge bg="danger">
                                        {membership.status}
                                    </Badge>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                ))}

                {loading && (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" variant="light" />
                        <span className="visually-hidden">
                            Loading members...
                        </span>
                    </div>
                )}

                {!loading && hasMore && (
                    <div className="text-center mt-3">
                        <Button
                            variant="outline-light"
                            onClick={handleLoadMore}
                        >
                            Load more
                        </Button>
                    </div>
                )}
                <h2 className="fs-3 fw-bold text-white mt-5 mb-4">
                    Moderation Applications
                </h2>
                {applicationsError && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setApplicationsError(null)}
                    >
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p className="mb-0">{applicationsError}</p>
                    </Alert>
                )}
                {applications.length === 0 ? (
                    <Alert variant="info">
                        No pending moderator applications for this community.
                    </Alert>
                ) : (
                    applications.map((application) => (
                        <ApplicationCard
                            key={application.applicationId}
                            application={application}
                            // Accept/Reject are site-admin-only (also enforced
                            // on the server); moderators still view the card and
                            // its history but see no decision buttons.
                            onAccept={isAdmin ? handleAccept : null}
                            onReject={isAdmin ? handleReject : null}
                            onViewHistory={handleViewHistory}
                        />
                    ))
                )}
            </Container>
            <Modal
                show={confirmModal.show}
                onHide={closeConfirmModal}
                centered
                aria-labelledby="confirm-modal-title"
            >
                <>
                    <Modal.Header closeButton>
                        <Modal.Title id="confirm-modal-title" className="fs-5">
                            Confirm{' '}
                            {confirmModal.action === 'ban' ? 'Ban' : 'Unban'}{' '}
                            User
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Are you sure you want to{' '}
                            {confirmModal.action === 'ban' ? 'ban' : 'unban'}{' '}
                            <strong>
                                {confirmModal.membership?.user?.username}
                            </strong>
                            ?
                        </p>
                        {confirmModal.action === 'ban' && (
                            <div className="mb-3">
                                <label
                                    htmlFor="ban-reason"
                                    className="form-label small fw-medium"
                                >
                                    Ban reason{' '}
                                    <span className="text-secondary">
                                        (optional)
                                    </span>
                                </label>
                                <InfoTooltip text="Only moderators and site admins can read this. It is kept as an internal record and is never shown to the banned member." />
                                <textarea
                                    id="ban-reason"
                                    className="form-control"
                                    rows={3}
                                    placeholder="Describe why this user is being banned..."
                                    value={confirmModal.reason}
                                    onChange={(e) =>
                                        setConfirmModal((prev) => ({
                                            ...prev,
                                            reason: e.target.value,
                                        }))
                                    }
                                />
                                <p className="text-danger small mt-3">
                                    This action bans the user, restricting them
                                    from posting or otherwise participating in
                                    this community, until they are manually
                                    unbanned.
                                </p>
                            </div>
                        )}
                        {confirmModal.action === 'unban' && (
                            <div className="mb-3">
                                <label
                                    htmlFor="ban-audit-fields"
                                    className="form-label small fw-medium"
                                >
                                    Ban Information:
                                </label>
                                <dl className="mb-0 small">
                                    <dt className="fw-medium">Banned by</dt>
                                    <dd className="text-muted">
                                        {confirmModal.membership?.bannedBy ||
                                            'Unknown'}
                                    </dd>
                                    <dt className="fw-medium">Reason</dt>
                                    <dd className="text-muted">
                                        {confirmModal.membership
                                            ?.bannedReason ||
                                            'No reason provided'}
                                    </dd>
                                    <dt className="fw-medium">Banned at</dt>
                                    <dd className="text-muted">
                                        {confirmModal.membership?.bannedAt
                                            ? formatFullDate(
                                                  confirmModal.membership
                                                      .bannedAt
                                              )
                                            : 'Unknown'}
                                    </dd>
                                </dl>
                                <p className="text-danger small mt-3">
                                    This action unbans the user, allowing them
                                    to post and otherwise participate in this
                                    community.
                                </p>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-dark"
                            onClick={closeConfirmModal}
                        >
                            Cancel
                        </Button>
                        {confirmModal.action === 'ban' && (
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => confirmBan(confirmModal.reason)}
                                aria-label={`Ban ${confirmModal.membership?.user?.username || 'this member'}`}
                            >
                                <i
                                    className="bi bi-slash-circle me-1"
                                    aria-hidden="true"
                                />
                                Ban
                            </Button>
                        )}
                        {confirmModal.action === 'unban' && (
                            <Button
                                variant="warning"
                                size="sm"
                                className="text-dark"
                                onClick={() => confirmUnban()}
                                aria-label={`Unban ${confirmModal.membership?.user?.username || 'this member'}`}
                            >
                                <i
                                    className="bi bi-slash-circle me-1"
                                    aria-hidden="true"
                                />
                                Unban
                            </Button>
                        )}
                    </Modal.Footer>
                </>
            </Modal>
            <Modal
                show={applicationModal.show}
                onHide={closeApplicationModal}
                centered
                aria-labelledby="application-modal-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="application-modal-title" className="fs-5">
                        {applicationModal.decision === 'accepted'
                            ? 'Accept'
                            : 'Reject'}{' '}
                        Application
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {applicationModal.error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() =>
                                setApplicationModal((prev) => ({
                                    ...prev,
                                    error: '',
                                }))
                            }
                        >
                            {applicationModal.error}
                        </Alert>
                    )}
                    <p>
                        Are you sure you want to{' '}
                        {applicationModal.decision === 'accepted'
                            ? 'accept'
                            : 'reject'}{' '}
                        the moderator application from{' '}
                        <strong>
                            @{applicationModal.application?.user?.username}
                        </strong>
                        ?
                    </p>
                    <div className="mb-3">
                        <label
                            htmlFor="decision-reason"
                            className="form-label small fw-medium"
                        >
                            Decision reason{' '}
                            {applicationModal.decision === 'rejected' ? (
                                <span className="text-danger">(required)</span>
                            ) : (
                                <span className="text-secondary">
                                    (optional)
                                </span>
                            )}
                        </label>
                        <textarea
                            id="decision-reason"
                            className={`form-control${rejectReasonMissing ? ' is-invalid' : ''}`}
                            rows={3}
                            placeholder="Add a note about this decision..."
                            value={applicationModal.reason}
                            onChange={(e) =>
                                setApplicationModal((prev) => ({
                                    ...prev,
                                    reason: e.target.value,
                                }))
                            }
                            aria-describedby={
                                rejectReasonMissing
                                    ? 'decision-reason-error'
                                    : undefined
                            }
                        />
                        {rejectReasonMissing && (
                            <div
                                id="decision-reason-error"
                                className="invalid-feedback"
                            >
                                Please provide a reason for rejecting this
                                application.
                            </div>
                        )}
                    </div>
                    {applicationModal.decision === 'accepted' && (
                        <p className="text-success small mb-0">
                            Accepting promotes this member to a moderator of
                            this community.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-dark"
                        onClick={closeApplicationModal}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={
                            applicationModal.decision === 'accepted'
                                ? 'success'
                                : 'danger'
                        }
                        size="sm"
                        onClick={confirmDecision}
                        disabled={rejectReasonMissing}
                        aria-label={`${
                            applicationModal.decision === 'accepted'
                                ? 'Accept'
                                : 'Reject'
                        } application from ${
                            applicationModal.application?.user?.username ||
                            'this applicant'
                        }`}
                    >
                        {applicationModal.decision === 'accepted'
                            ? 'Accept'
                            : 'Reject'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={historyModal.show}
                onHide={closeHistoryModal}
                centered
                scrollable
                aria-labelledby="history-modal-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="history-modal-title" className="fs-5">
                        Recent applications
                        {historyModal.application?.user?.username
                            ? ` from @${historyModal.application.user.username}`
                            : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyModal.application?.applicationHistory?.length ? (
                        <ul className="list-unstyled mb-0">
                            {historyModal.application.applicationHistory.map(
                                (entry) => {
                                    const badge =
                                        STATUS_BADGE[entry.status] ||
                                        STATUS_BADGE.dismissed;
                                    return (
                                        <li
                                            key={entry.applicationId}
                                            className="border-bottom pb-3 mb-3"
                                        >
                                            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                                <Badge
                                                    bg={badge.bg}
                                                    className={badge.className}
                                                >
                                                    {badge.label}
                                                </Badge>
                                                {entry.resolvedAt && (
                                                    <span className="text-body-secondary small">
                                                        Resolved{' '}
                                                        {formatFullDate(
                                                            entry.resolvedAt
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mb-1 small fw-semibold text-body-secondary">
                                                Reason
                                            </p>
                                            <div className="border rounded bg-light p-2">
                                                {entry.decisionReason || (
                                                    <span className="text-body-secondary fst-italic">
                                                        No reason given
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                }
                            )}
                        </ul>
                    ) : (
                        <p className="mb-0 text-body-secondary">
                            No past applications.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-dark" onClick={closeHistoryModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showRequestConfirm}
                onHide={() => setShowRequestConfirm(false)}
                centered
                aria-labelledby="request-confirm-modal-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title
                        id="request-confirm-modal-title"
                        className="fs-5"
                    >
                        Request moderators
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Request that{' '}
                        <strong>{selectedCommunity?.community?.name}</strong> be
                        flagged as needing moderators? A site admin will review
                        your request.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-dark"
                        onClick={() => setShowRequestConfirm(false)}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleRequestModerators}>
                        Send request
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

ModeratorMembers.propTypes = {
    communities: PropTypes.arrayOf(
        PropTypes.shape({
            communityId: PropTypes.string,
            community: PropTypes.shape({
                name: PropTypes.string,
            }),
        })
    ),
};
